import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminPageShell from '../components/AdminPageShell';
import AdminTabs from '../components/AdminTabs';
import UsersView from './accounts/UsersView';
import OrganizationsView from './accounts/OrganizationsView';
import WorkspacesView from './accounts/WorkspacesView';
import ActivityView from './accounts/ActivityView';

const TABS = [
  { key: 'users', label: 'Users' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'workspaces', label: 'Workspaces' },
  { key: 'activity', label: 'Activity' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Accounts — the consolidated replacement for the legacy Users,
 * Organizations, Workspaces, and User Analytics tools. One tool because it's
 * one relational graph: users belong to organizations, organizations own
 * workspaces, and activity describes all three.
 * Requirements: Documentation/ACCOUNTS_TOOL_REQUIREMENTS.md
 */
const AccountsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = TABS.some((tab) => tab.key === requestedTab)
    ? (requestedTab as TabKey)
    : 'users';

  return (
    <AdminPageShell
      title="Accounts"
      description="Users, organizations, and workspaces — one connected view"
    >
      <AdminTabs
        tabs={[...TABS]}
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
      />

      {activeTab === 'users' && <UsersView />}
      {activeTab === 'organizations' && <OrganizationsView />}
      {activeTab === 'workspaces' && <WorkspacesView />}
      {activeTab === 'activity' && <ActivityView />}
    </AdminPageShell>
  );
};

export default AccountsPage;
